import {NoneKPIColumns, SelectorTableColumnDef, SelectorTableColumnDefs} from './GeneralSelectorTableStore';
import {observable} from 'mobx';
import {DescriptiveKPIAdditions, KPI, KPIGroupKey} from '../kpi';
import {HistoryHandler, HistoryItem} from './MemoryBarStore';
import {AnalyticsTab} from './store';
import {CompareMode, TableCompareModes} from './CompareStore';

const toggle = (value: boolean, on?: boolean) => (on !== undefined ? on : !value);

type ColumnKeys = KPI | KPIGroupKey | DescriptiveKPIAdditions | NoneKPIColumns;

interface SelectorTableLayoutDef {
    name: string;
    label: string;
    columns: ColumnKeys[];
    hiddenColumns?: ColumnKeys[];
}

type SelectorTableLayoutDefs = SelectorTableLayoutDef[];

export interface ColumnGroupInfo {
    id: number;
    label: string;
    firstRel: number;
    lastRel: number;
    isOpen: boolean;
    isVisible: boolean;
    withToggleButton: boolean;
}

interface Group {
    name: string;
    label: string;
    columns: string[];
    isOpen: boolean;
    columnIsVisible: {[column: string]: boolean};
}

interface ChannelComparePackageLayout {
    compareIsEnabled: boolean;
    getCompareModeValueForKpi: (kpi: KPI) => TableCompareModes;
    getMultiChannelReplacementRows: (kpi: KPI) => SelectorTableColumnDef[];
}

export class SelectorTableLayout implements HistoryHandler {
    private readonly getColumnDefs: () => SelectorTableColumnDefs;
    private readonly getChannelComparePackage: () => ChannelComparePackageLayout;
    private readonly onChange: (layoutState: string) => void;
    private readonly dependentAnalyticsTab: AnalyticsTab;
    private layoutDef: SelectorTableLayoutDefs;
    private columnGroupInfos!: ColumnGroupInfo[]; // Built in constructor
    private fixedColumnCount = 0;
    @observable private layoutState = '';
    @observable private visibleColumnDefs!: SelectorTableColumnDef[]; // Built in constructor
    @observable private groups: Group[] = [];

    constructor(
        getColumnDefs: () => SelectorTableColumnDefs,
        getChannelComparePackage: () => ChannelComparePackageLayout,
        layoutDef: SelectorTableLayoutDefs,
        dependentAnalyticsTab: AnalyticsTab,
        onChange: (layoutState: string) => void,
        layoutState: string,
        private recomputeMultiGridSize: () => void
    ) {
        this.getColumnDefs = getColumnDefs;
        this.getChannelComparePackage = getChannelComparePackage;
        this.layoutDef = layoutDef;
        this.onChange = onChange;
        this.dependentAnalyticsTab = dependentAnalyticsTab;
        this.layoutState = layoutState;
        this.reset(false);
        this.updateVisibleColumns();
    }

    public getLayoutState() {
        return this.layoutState;
    }

    public getPartialHistory = (): Partial<HistoryItem> => ({
        tableLayout: this.layoutState,
    });

    public applyHistory = (history: HistoryItem): void => {
        if (this.dependentAnalyticsTab !== history.analyticsTab) {
            return;
        }

        this.setLayoutState(history.tableLayout);
        this.updateVisibleColumns();
        this.updateStateString();
        this.recomputeMultiGridSize();
    };

    private reset(allColumnsVisible: boolean) {
        this.groups = [];
        this.layoutDef.forEach((groupDef, index) => {
            const group: Group = {name: groupDef.name, label: groupDef.label, columns: [], isOpen: false, columnIsVisible: {}};
            groupDef.columns.forEach((column) => {
                group.columns.push(column);
                group.columnIsVisible[column] = allColumnsVisible || !groupDef.hiddenColumns || groupDef.hiddenColumns.indexOf(column) < 0;
            });
            this.groups[index] = group;
        });
    }

    public updateVisibleColumns() {
        const channelComparePackage = this.getChannelComparePackage();
        this.visibleColumnDefs = [];
        this.columnGroupInfos = [];
        this.fixedColumnCount = 0;
        const columnDefs = this.getColumnDefs();
        let columnIndex = 0;
        this.groups.forEach((group, index) => {
            const firstIndex = columnIndex;
            let allColumnsVisible = true;
            group.columns.forEach((column) => {
                if (!this.groups[index].columnIsVisible[column]) {
                    allColumnsVisible = false;
                    if (!group.isOpen) {
                        return;
                    }
                }

                if (channelComparePackage && channelComparePackage.compareIsEnabled) {
                    const compareModesForKPI = channelComparePackage.getCompareModeValueForKpi(column as KPI);

                    if (compareModesForKPI && compareModesForKPI.absDiffMode) {
                        this.visibleColumnDefs.push({...columnDefs[column], compareColumnInfo: {compareMode: CompareMode.AbsDiffMode}});
                        columnIndex++;
                    }
                    if (compareModesForKPI && compareModesForKPI.ratioMode) {
                        this.visibleColumnDefs.push({...columnDefs[column], compareColumnInfo: {compareMode: CompareMode.RatioMode}});
                        columnIndex++;
                    }
                    if (compareModesForKPI && compareModesForKPI.multiChannelColumnsMode) {
                        const compareColumnsForKpi = channelComparePackage.getMultiChannelReplacementRows(column as KPI);
                        this.visibleColumnDefs = [...this.visibleColumnDefs, ...compareColumnsForKpi];
                        columnIndex += compareColumnsForKpi.length;
                    }
                } else {
                    this.visibleColumnDefs.push(columnDefs[column]);
                    columnIndex++;
                }
            });
            if (index === 0) {
                this.fixedColumnCount = columnIndex;
            }
            for (let i = firstIndex; i < columnIndex; i++) {
                this.columnGroupInfos[i] = {
                    id: index,
                    label: group.label,
                    firstRel: firstIndex - i,
                    lastRel: columnIndex - i - 1,
                    isOpen: group.isOpen,
                    isVisible: this.groups[index].columnIsVisible[this.visibleColumnDefs[i].dataKey],
                    withToggleButton: !allColumnsVisible,
                };
            }
        });
    }

    private updateStateString() {
        // We don't use JSON here because we need a format that works with the next
        // versions of this software. Hence we record what is hidden from the layout
        // rather than what is visible.
        let layoutState = 'v1';
        this.groups.forEach((group) => {
            if (group.isOpen) {
                layoutState += '|o' + group.name;
            }
            group.columns.forEach((column) => {
                if (!group.columnIsVisible[column]) {
                    layoutState += '|h' + column;
                }
            });
        });
        if (this.layoutState !== layoutState) {
            this.layoutState = layoutState;
            this.onChange(layoutState);
        }
    }

    private forceIntegrity() {
        this.groups.forEach((group) => {
            if (!group.columns.some((column) => group.columnIsVisible[column])) {
                group.columnIsVisible[group.columns[0]] = true;
            }
        });
    }

    public setLayoutState(layoutState?: string) {
        if (!layoutState) {
            return;
        }
        this.reset(true);
        const columnGroup: {[key: string]: Group} = {};
        this.groups.forEach((group) => {
            group.columns.forEach((column) => (columnGroup[column] = group));
        });
        layoutState.split('|').forEach((part) => {
            const type = part.substr(0, 1);
            const arg = part.substr(1);
            if (type === 'o') {
                const group = this.groups.find((group_) => group_.name === arg);
                if (group) {
                    group.isOpen = true;
                }
                return;
            }
            if (type === 'h') {
                if (arg in columnGroup) {
                    columnGroup[arg].columnIsVisible[arg] = false;
                }
            }
        });
        this.forceIntegrity();
        this.updateVisibleColumns();
    }

    public toggleGroup(groupId: number, on?: boolean) {
        const group = this.groups[groupId];
        group.isOpen = toggle(group.isOpen, on);
        this.updateVisibleColumns();
        this.updateStateString();
    }

    public toggleGroupColumn(groupId: number, column: string, on?: boolean) {
        const group = this.groups[groupId];
        group.columnIsVisible[column] = toggle(group.columnIsVisible[column], on);
        this.forceIntegrity();
        this.updateVisibleColumns();
        this.updateStateString();
    }

    public getVisibleColumnDefs() {
        return this.visibleColumnDefs;
    }

    public getGroup(index: number) {
        return this.groups[index];
    }

    public getColumnGroupInfos() {
        return this.columnGroupInfos;
    }

    public getFixedColumnCount() {
        return this.fixedColumnCount;
    }
}



// WEBPACK FOOTER //
// ./src/stores/SelectorTableLayout.ts